import { useIsRestoring } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { useEffect, useMemo, useState } from 'react';
import { RouterProvider } from 'react-router';
import { Button, EmptyState } from '@/app/kit';
import { shellCopy } from '@/copy/shell';
import { type DisplayMode, displayMode, onDisplayModeChange } from '@/lib/displayMode';
import { codeFromPath } from '@/lib/invite';
import { attachLaunchSink } from '@/lib/launchQueue';
import { startOutbox } from '@/lib/outbox';
import { persistOptions, queryClient, runOfflinePrefetch } from '@/lib/query';
import { useSignals } from '@/lib/signals';
import { ToastHost } from '@/ui';
import { InstallGate } from './gate/InstallGate';
import { initModules } from './registry';
import { appRouter } from './router';
import { Splash } from './screens/System';
import { noSignal, useMe, watchSignOut } from './session';
import { UpdatePrompt } from './UpdatePrompt';
import { Welcome } from './welcome/Welcome';

/**
 * The app shell's top decision, in this order:
 *   1. Not the installed app (or the server said 428): the install gate. No redirect, no route.
 *   2. Installed but nobody signed in on this phone: Welcome (paste or type the crew code).
 *   3. Signed in: the router with the five tabs.
 */

/** Dev only: ?nogate skips the gate. import.meta.env.DEV is false in builds, so this branch is dropped. */
function devBypass(): boolean {
  if (import.meta.env.DEV) {
    return new URLSearchParams(window.location.search).has('nogate');
  }
  return false;
}

function useDisplayModeState(): DisplayMode {
  const [mode, setMode] = useState(displayMode);
  useEffect(() => onDisplayModeChange(setMode), []);
  return mode;
}

export function App() {
  const mode = useDisplayModeState();
  const gateReason = useSignals((s) => s.gateReason);
  const code = useMemo(() => codeFromPath(window.location.pathname), []);

  if ((!mode.standalone && !devBypass()) || gateReason) {
    return <InstallGate code={code} serverSaid={gateReason === 'server'} />;
  }
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions()}>
      <Signed code={code} />
      <ToastHost />
      <UpdatePrompt />
    </PersistQueryClientProvider>
  );
}

function Signed({ code }: { code: string | null }) {
  const restoring = useIsRestoring();
  const me = useMe();
  useEffect(() => watchSignOut(), []);

  if (restoring || (me.isPending && me.fetchStatus === 'fetching')) return <Splash />;
  if (me.data) return <Authed />;
  if (me.data === null) return <Welcome initialCode={code} />;
  // No cached answer and the phone has no signal: Welcome still renders (the shell is precached), and
  // the query refetches when the signal returns. A server that answers with an error gets the screen below.
  if (me.isPending || noSignal(me.error)) return <Welcome initialCode={code} />;
  return (
    <main className="mx-auto grid min-h-dvh max-w-md place-items-center p-6">
      <EmptyState
        title={shellCopy.routeError.title}
        body={navigator.onLine ? shellCopy.routeError.body : shellCopy.offline}
        art="whistle"
        action={
          <Button variant="primary" onClick={() => void me.refetch()}>
            {shellCopy.routeError.action}
          </Button>
        }
      />
    </main>
  );
}

function Authed() {
  useEffect(() => {
    initModules();
    // A shortcut or captured link opened while the app runs only focuses it (manifest launch_handler);
    // this is what moves the router to the target.
    const detachLaunch = attachLaunchSink((path) => {
      if (path !== window.location.pathname + window.location.search + window.location.hash) {
        void appRouter().navigate(path);
      }
    });
    const stopOutbox = startOutbox();
    void runOfflinePrefetch();
    const onOnline = () => void runOfflinePrefetch();
    window.addEventListener('online', onOnline);
    return () => {
      detachLaunch();
      stopOutbox();
      window.removeEventListener('online', onOnline);
    };
  }, []);
  return <RouterProvider router={appRouter()} />;
}
